// Render-only mirrors of the Firestore documents. The client NEVER implements
// game rules — it displays state and sends actions. These shapes match
// functions/src/gamecore/models.ts and the projections in callable/shared.ts.

export type Role = 'director' | 'security' | 'rebel' | 'fixer';

export type CardKind =
  | 'hack'
  | 'firewall'
  | 'cipherShield'
  | 'rig'
  | 'overclock'
  | 'longwave'
  | 'raid'
  | 'downtime'
  | 'medPatch'
  | 'payday'
  | 'duel'
  | 'emp'
  | 'virus'
  | 'siphon'
  | 'stim'
  | 'trace'
  | 'airGap'
  | 'killSwitch'
  | 'backchannel'
  | 'fieldPatch'
  | 'coldReboot'
  | 'packetStorm';

export interface Card {
  id: string;
  kind: CardKind;
}

export type RoomStatus = 'lobby' | 'playing' | 'finished';

export interface Room {
  id: string;
  code: string;
  hostUid: string;
  status: RoomStatus;
  turnLimit: number;
  botDifficulty: string;
  /** Host opted this lobby into the public games browser. */
  isPublic?: boolean;
  /** Current game's seed (set at start) — keys the archive doc and coin payout. */
  seed?: string;
}

// publicRooms/{roomId} — a Cloud-Functions projection of open public lobbies.
export interface PublicRoom {
  roomId: string;
  code: string;
  /** 'lobby' rooms can be joined; 'playing' rooms can be spectated. */
  status: 'lobby' | 'playing';
  hostName: string;
  hostCountry?: string | null;
  playerCount: number;
  maxPlayers: number;
  turnLimit: number;
  botDifficulty: string;
  /** Epoch ms of the last projection write; the browser hides stale lobbies. */
  updatedAtMs: number;
}

// rooms/{id}/players/{uid}
export interface PublicPlayer {
  uid: string;
  displayName: string;
  photoURL?: string | null;
  bot?: boolean;
  seatIndex: number;
  integrity: number;
  maxIntegrity: number;
  alive: boolean;
  equippedGear: Card[];
  handCount: number;
  characterId?: string;
  roleRevealed: boolean;
  revealedRole?: Role | null;
  abilityUsed?: boolean;
  /** Earned cosmetic nick-colour id (see game/cosmetics.ts); absent = default. */
  nameColor?: string;
  /** Shop-bought avatar-frame id (see game/shop.ts); absent/null = no frame. */
  frame?: string | null;
}

export type Phase = 'draw' | 'play' | 'discard' | 'finished';
export type Winner = 'directorSide' | 'rebels' | 'fixer';

export interface PendingAttack {
  attackerUid: string;
  targetUid: string;
  cardId?: string;
  effectKind?: CardKind;
  targetCardId?: string;
  cardIds?: string[];
}

// rooms/{id}/state/current
export interface GameState {
  phase: Phase;
  activeSeat: number;
  turnCount: number;
  hacksPlayedThisTurn: number;
  deckCount: number;
  discardTop?: Card | null;
  pendingAttack?: PendingAttack | null;
  /** Each line is JSON.stringify(LogEvent); render via i18n renderLogLine. */
  turnLog: string[];
  stats?: Record<string, PlayerStats>;
  winner?: Winner | null;
  turnLimit?: number;
  /** Epoch ms after which the current actor's turn is auto-passed. */
  turnDeadline?: number | null;
}

export interface PlayerStats {
  dealt: number;
  taken: number;
  spikes: number;
  blocks: number;
  heals: number;
}

/** Mirror of the server's structured log events (see functions gamecore). */
export type LogEvent =
  | { t: 'start'; n: number; d: string }
  | { t: 'draw'; p: string }
  | { t: 'salvage'; p: string; k: CardKind }
  | { t: 'spike'; a: string; b: string }
  | { t: 'shield'; p: string }
  | { t: 'mayFw'; p: string }
  | { t: 'fw'; p: string }
  | { t: 'burst'; p: string }
  | { t: 'ride'; p: string }
  | { t: 'duel'; a: string; b: string; l: string }
  | { t: 'raid'; a: string; b: string }
  | { t: 'siphon'; a: string; b: string }
  | { t: 'stim'; p: string }
  | { t: 'trace'; a: string; b: string; r: Role }
  | { t: 'airGap'; p: string; k: CardKind }
  | { t: 'killSwitch'; a: string; b: string; k: CardKind }
  | { t: 'backchannel'; a: string; b: string }
  | { t: 'fieldPatch'; a: string; b: string }
  | { t: 'coldReboot'; p: string; n: number }
  | { t: 'packetStorm'; p: string }
  | { t: 'emp'; p: string }
  | { t: 'med'; p: string }
  | { t: 'pay'; p: string }
  | { t: 'equip'; p: string; k: CardKind }
  | { t: 'plant'; p: string; k: CardKind; b: string }
  | { t: 'mustDiscard'; p: string; n: number }
  | { t: 'discard'; p: string; n: number }
  | { t: 'dmg'; p: string; n: number }
  | { t: 'surge'; p: string; n: number }
  | { t: 'reboot'; p: string }
  | { t: 'leech'; a: string; b: string }
  | { t: 'regen'; p: string }
  | { t: 'lash'; a: string; b: string }
  | { t: 'bounty'; p: string }
  | { t: 'penalty'; p: string }
  | { t: 'flat'; p: string; r: Role }
  | { t: 'leave'; p: string }
  | { t: 'virus'; p: string }
  | { t: 'virusJump'; p: string }
  | { t: 'virusOut' }
  | { t: 'dtSkip'; p: string }
  | { t: 'dtFree'; p: string }
  | { t: 'reshuffle' }
  | { t: 'toHit'; p: string }
  | { t: 'toSkip'; p: string }
  | { t: 'win'; w: Winner };

export function parseLogEvent(line: string): LogEvent | null {
  if (!line.startsWith('{')) return null;
  try {
    const ev = JSON.parse(line);
    return typeof ev?.t === 'string' ? (ev as LogEvent) : null;
  } catch {
    return null;
  }
}

// rooms/{id}/private/{uid} — owner-read only
export interface PrivateState {
  uid: string;
  role: Role;
  hand: Card[];
}

// A player action sent to the submitAction callable.
export type PlayerAction =
  | { type: 'draw' }
  | { type: 'playCard'; cardId: string; targetUid?: string; targetCardId?: string; cardIds?: string[] }
  | { type: 'respond'; cardId?: string }
  | { type: 'discard'; cardIds: string[] }
  | { type: 'endPhase' };

// Cards that require another player as a target when played.
const TARGETED: ReadonlySet<CardKind> = new Set<CardKind>([
  'hack',
  'duel',
  'raid',
  'downtime',
  'virus',
  'siphon',
  'trace',
  'killSwitch',
  'backchannel',
  'fieldPatch',
]);

export function needsTarget(kind: CardKind): boolean {
  return TARGETED.has(kind);
}
